import {
  Autocomplete,
  AutocompleteRenderInputParams,
  InputAdornment,
} from '@mui/material';

import { Search } from '@mui/icons-material';
import { StyledContainer, StyledTextField } from './ResourceFilter.style';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { getterAdminResourceFilter } from '~/store/resource/resourceGetter';
import {
  getHeader,
  defaultOption,
  fuzzySearchFieldsOption,
  fuzzySearchFieldsText,
} from '~/utils/constants';
import { useAppDispatch } from '~/store';
import { setSearchCondition } from '~/store/resource';
import { TUniqResourceProps } from '~/api/oneBoxV3/oneBoxV3.type';
import { DatePicker } from '@mui/x-date-pickers';
import { TSearchCondition } from '~/store/resource/resource.type';
import { endOfDate, startOfDate } from '~/utils/helper/format';
import { THandleSelectResourceOption } from '~/components/Admin/AdminComponent.type';
import { useLazyDropDownListQuery } from '~/api/oneBoxV3';
import { useEffect, CompositionEvent, useState } from 'react';
import dayjs from 'dayjs';
import { SearchCategory, adminTab } from '~/types/enum';

const renderInput = (
  params: AutocompleteRenderInputParams,
  id: keyof TSearchCondition,
) => <StyledTextField color="info" {...params} label={getHeader(id)} />;

const getOptionLabel = (option: string) => (option === 'all' ? '全部' : option);

export const ResourceFilter = ({ tab }: { tab: adminTab }) => {
  const [dropDownList, setDropDownList] =
    useState<TUniqResourceProps>(defaultOption);
  const {
    categories,
    versions,
    years,
    eduCodes,
    subjects,
    grades,
    fuzzySearch,
    fuzzySearchFields,
    dateFrom,
    dateTo,
    isLaunch,
  } = useSelector(getterAdminResourceFilter);
  const [fuzzySearchValue, setFuzzySearchValue] = useState(fuzzySearch || '');
  const [fuzzySearchKeyDownCode, setFuzzySearchKeyDownCode] = useState('');
  const [reDropDownList] = useLazyDropDownListQuery();
  const dispatch = useAppDispatch();
  const [isComposing, setIsComposing] = useState(false);
  const categoryOptions =
    tab === adminTab.school
      ? [SearchCategory.SCHOOL]
      : [SearchCategory.CRAM_SCHOOL, SearchCategory.INTERSECTION];

  const handleSelectOption = ({
    event,
    id,
    customOptions,
  }: THandleSelectResourceOption) => {
    const idx = Number((event.target as HTMLButtonElement).dataset.optionIndex);
    dispatch(
      setSearchCondition({
        [`${id}`]: idx === 0 ? 'all' : customOptions[`${idx - 1}`],
        ...(id === 'eduCodes' && { grades: 'all' }),
      }),
    );
  };

  const handleCategorySelectOption = ({
    id,
    customOptions,
  }: THandleSelectResourceOption) => {
    dispatch(
      setSearchCondition({
        [`${id}`]: customOptions ?? categories,
        ...(id === 'eduCode' && { grade: 'all' }),
      }),
    );
  };

  useEffect(() => {
    const getDropDownList = () => {
      void (async () => {
        try {
          const dropDownData = await reDropDownList({
            isLaunch,
            categories:
              categories === SearchCategory.INTERSECTION
                ? [SearchCategory.CRAM_SCHOOL, SearchCategory.SCHOOL]
                : [categories],
            resourceProps: [
              'versions',
              'years',
              'eduCodes',
              'subjects',
              'grades',
              'resources1',
            ],
          }).unwrap();
          if (!dropDownData.err)
            setDropDownList(dropDownData.data?.uniqResourceProps ?? {});
        } catch (error) {
          console.log('dropDownListApi error', error);
        }
      })();
    };
    getDropDownList();
  }, [categories, isLaunch]);

  useEffect(() => {
    const debouncedUpdate = debounce((payload: string) => {
      dispatch(setSearchCondition({ fuzzySearch: payload }));
    }, 300);
    if (!isComposing && fuzzySearchKeyDownCode === 'Enter') {
      debouncedUpdate(fuzzySearchValue);
    }

    return () => debouncedUpdate.cancel();
  }, [fuzzySearchValue, fuzzySearchKeyDownCode]);

  // 偵測是否正在輸入中文
  const handleComposition = (e: CompositionEvent<HTMLInputElement>) => {
    if (e.type === 'compositionstart') {
      setIsComposing(true);
    } else if (e.type === 'compositionend') {
      setIsComposing(false);
      setFuzzySearchKeyDownCode('');
    }
  };

  return (
    <StyledContainer>
      <Autocomplete
        disablePortal
        options={[...categoryOptions]}
        size="small"
        getOptionLabel={option =>
          option === SearchCategory.CRAM_SCHOOL ? '補教 (全部)' : option
        }
        renderInput={params => renderInput(params, 'categories')}
        value={categories}
        onChange={(event, value) => {
          handleCategorySelectOption({
            event,
            id: 'categories',
            customOptions: value as SearchCategory,
          });
        }}
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.versions ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'versions')}
        value={versions}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'versions',
            customOptions: dropDownList.versions ?? [],
          })
        }
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.years ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'years')}
        value={years}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'years',
            customOptions: dropDownList.years ?? [],
          })
        }
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.eduCodes ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'eduCodes')}
        value={eduCodes}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'eduCodes',
            customOptions: dropDownList.eduCodes ?? [],
          })
        }
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.subjects ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'subjects')}
        value={subjects}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'subjects',
            customOptions: dropDownList.subjects ?? [],
          })
        }
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.grades ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'grades')}
        value={grades}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'grades',
            customOptions: dropDownList.grades ?? [],
          })
        }
      />
      <DatePicker
        label={getHeader('dateFrom')}
        format="YYYY-MM-DD"
        value={dateFrom ? dayjs(dateFrom) : null}
        onChange={newValue => {
          newValue &&
            dispatch(setSearchCondition({ dateFrom: startOfDate(newValue) }));
        }}
        slotProps={{ textField: { size: 'small' } }}
        disableFuture
      />
      <DatePicker
        label={getHeader('dateTo')}
        format="YYYY-MM-DD"
        value={dateTo ? dayjs(dateTo) : null}
        onChange={newValue => {
          newValue &&
            dispatch(setSearchCondition({ dateTo: endOfDate(newValue) }));
        }}
        slotProps={{ textField: { size: 'small' } }}
        disableFuture
      />
      <Autocomplete
        disablePortal
        options={fuzzySearchFieldsOption}
        size="small"
        getOptionLabel={option => fuzzySearchFieldsText[option]}
        renderInput={params => renderInput(params, 'fuzzySearchFields')}
        value={fuzzySearchFields ? fuzzySearchFields[0] : undefined}
        onChange={(event, value) => {
          if (!value) return;
          dispatch(
            setSearchCondition({
              fuzzySearchFields: [value],
            }),
          );
        }}
      />
      <StyledTextField
        placeholder={getHeader('fuzzySearch')}
        sx={{ gridColumnStart: '4', gridColumnEnd: '7' }}
        size="small"
        value={fuzzySearchValue}
        onChange={event => setFuzzySearchValue(event.target.value)}
        onKeyDown={event => setFuzzySearchKeyDownCode(event.key)}
        onCompositionUpdate={handleComposition}
        onCompositionEnd={handleComposition}
        onCompositionStart={handleComposition}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />
    </StyledContainer>
  );
};
